import React, { useEffect, useState, useCallback } from 'react'
import { Motion, spring } from 'react-motion'
import cx from 'classnames'
import { bool, object } from 'prop-types'
import networkService from 'src/app/network'
import EventsService from 'src/utils/events-service'
import Storage from 'src/utils/storage'
import { isIE, updateWidgetPopupTimestamp, isWidgetPopupAllowed } from 'src/utils/helpers'
import config from './config'
import WidgetFrame from './WidgetFrame'
import WidgetPopup from './WidgetPopup'
import Button from './WidgetButton'
import App from 'src/modules/App'

const styles = `
.widgetFrameInner {
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.widgetBaseContainer {
  width: 380px;
  height: calc(100% - 20px - 80px - 20px);
  max-height: 640px;
  min-height: 215px;
  position: fixed;
  bottom: 100px;
  right: 20px;
  box-shadow: 0 5px 40px rgba(0, 0, 0, 0.15);
  z-index: 999990;
}

.widgetBaseContainerActiveFullSize {
  position: fixed;
  z-index: 999999991;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100% !important;
  max-height: 100% !important;
  width: 100% !important;
}
`

let windowResizeTimeout = null

const Widget = ({ openOnLoad, colors, sound, css, onIsActiveChange, initialFeature, ...rest }) => {
  const [isActive, setIsActive] = useState(false)
  const [isFullSize, setIsFullSize] = useState(false)
  const [isAnimationActive, setIsAnimationActive] = useState(false)
  // To know when widget popups up for the first time
  const [isFirstActivation, setIsFirstActivation] = useState(true)

  // Check if closed in last 12h
  const isPopupAllowed = isWidgetPopupAllowed()

  // Checks if widget full size is needed based on parent window size
  const updateFullSizeState = active => {
    const cls = 'kiosk-widget-mobile-full-size'
    const width = window.innerWidth
    const height = window.innerHeight

    // When widget is open on small screen
    if (active && (width < 450 || (width < 900 && height < 450))) {
      // Add class to main/parent <html> element
      document.documentElement.classList.add(cls)
      setIsFullSize(true)
    } else {
      // Remove class from main/parent <html> element
      document.documentElement.classList.remove(cls)
      setIsFullSize(false)
    }
  }

  // Set animation as active whenever widget is being opened or closed
  const setActiveState = useCallback(
    active => {
      if (windowResizeTimeout) {
        clearTimeout(windowResizeTimeout)
      }

      setIsAnimationActive(true)

      setIsActive(active)

      // On open
      if (active === true) {
        if (isFirstActivation === true) {
          // Injects authorization token so that visit event can be correctly registered
          networkService.addAuthorizationHeader()

          EventsService.registerEvent({ isWidget: true }, 'access', 'visit')

          setIsFirstActivation(false)

          // Allow for "initial widget feature" to be opened again
          Storage.removeSessionItem('isInitialWidgetFeatureLoaded')
        }
      }

      // On close
      if (active === false) {
        // Reset last closed timestamp
        updateWidgetPopupTimestamp()
      }

      if (onIsActiveChange && typeof onIsActiveChange === 'function') {
        onIsActiveChange(active)
      }
    },
    [isFirstActivation, onIsActiveChange],
  )

  useEffect(() => {
    if (isPopupAllowed && openOnLoad) {
      setTimeout(() => {
        if (!isActive) {
          setActiveState(true)
        }
      }, 5000)
    }
  }, [isActive, setActiveState, isPopupAllowed, openOnLoad])

  // Handle window resize
  useEffect(() => {
    // Function to call on window resize
    const windowSizeCalculations = () => {
      if (windowResizeTimeout) {
        clearTimeout(windowResizeTimeout)
      }

      // Run with 200ms delay
      windowResizeTimeout = setTimeout(() => {
        updateFullSizeState(isActive)
      }, 200)
    }

    // On mount - or isActive change
    updateFullSizeState(isActive)
    window.addEventListener('resize', windowSizeCalculations)

    // On unmount
    return () => {
      window.removeEventListener('resize', windowSizeCalculations)
    }
  }, [isActive])

  const handleCoreContainerStyles = () => {
    const iframeContainer = document.querySelector('#kiosk-container')

    // Work in progress...
    // Can be finished or removed if better solution is found.
    // Until then, injecting `build/widget/static/css/main.css` to iframe's head is used (below).
    // TODO: ant is removed
    // This didn't work because webpack's style-loader didn't compile CSS for ant design elements.
    //
    // Attach CSS style element into iframe's head element
    // let iframeLoadedEvent = new Event("kioskWidgetIframeLoaded", { bubbles: true })
    // // If new Event doesn't work in IE for example, try using:
    // // let iframeLoadedEvent = document.createEvent('CustomEvent')
    // // iframeLoadedEvent.initCustomEvent("kioskWidgetIframeLoaded", { bubbles: true })
    // iframeContainer.dispatchEvent(iframeLoadedEvent)

    // Append core style
    const coreStyleEl = document.createElement('style')
    coreStyleEl.id = 'kiosk-core-styles'
    coreStyleEl.appendChild(document.createTextNode(css.coreStyles))
    iframeContainer.contentDocument.head.appendChild(coreStyleEl)

    // Append branding style
    const brandingStyleEl = document.createElement('style')
    brandingStyleEl.id = 'kiosk-branding-color'
    brandingStyleEl.appendChild(document.createTextNode(css.brandingStyles))
    iframeContainer.contentDocument.head.appendChild(brandingStyleEl)
  }

  const appIframeHolderClassName = cx({
    ['widgetBaseContainer']: true,
    ['widgetBaseContainerActiveFullSize']: isActive && isFullSize,
  })
  // console.log('first', styles)
  return (
    <React.Fragment>
      <style>{styles}</style>
      {!isIE() && sound && isPopupAllowed && (
        <iframe
          title='notification-sound'
          src={config.notificationSound}
          allow='autoplay'
          id='audio'
          style={{ display: 'none' }}
        />
      )}
      <Motion
        defaultStyle={{
          y: 40,
          opacity: 0,
        }}
        style={{
          y: spring(isActive ? 0 : 40, config.presets.default),
          opacity: spring(isActive ? 1 : 0, config.presets.default),
        }}
        onRest={() => setIsAnimationActive(false)}
      >
        {interpolatingStyles => (
          <div
            style={{
              display: `${isActive || isAnimationActive ? 'block' : 'none'}`,
            }}
          >
            <div
              className={appIframeHolderClassName}
              style={{
                opacity: interpolatingStyles.opacity,
                transform: `translateY(${interpolatingStyles.y}px)`,
              }}
            >
              <WidgetFrame
                id='kiosk-container'
                title='Kiosk app'
                colors={colors}
                isActiveAndFullSize={isActive && isFullSize}
                contentDidMount={handleCoreContainerStyles}
              >
                <WidgetPopup>
                  <App
                    isWidget={true}
                    isWidgetFullSize={isFullSize}
                    isWidgetActive={isActive}
                    handleCloseWidget={() => setActiveState(false)}
                    initialWidgetFeature={initialFeature}
                    {...rest}
                  />
                </WidgetPopup>
              </WidgetFrame>
            </div>
          </div>
        )}
      </Motion>
      <Button
        colors={colors}
        active={isActive}
        isActiveAndFullSize={isActive && isFullSize}
        onClick={() => setActiveState(!isActive)}
      />
    </React.Fragment>
  )
}

Widget.propTypes = {
  colors: object.isRequired,
  openOnLoad: bool.isRequired,
  css: object.isRequired,
  // Provided from withStyles()
  // classes: PropTypes.object.isRequired,
  // theme: PropTypes.object.isRequired,
}

Widget.defaultProps = {
  openOnLoad: false,
  sound: false,
}

export default Widget
